import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, RefreshCw, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const CAD = (n) => n == null ? "—" : new Intl.NumberFormat("en-CA",
  { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

const YoYPill = ({ pct }) => {
  if (pct == null) return null;
  const up = pct >= 0;
  return (
    <span className={`text-xs px-2 py-0.5 font-semibold uppercase tracking-wider ${up ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}% YoY
    </span>
  );
};

export const MarketBriefing = ({ city = "all", onNeighbourhoodPick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [err, setErr] = useState("");

  const fetchBriefing = async (force = false) => {
    setLoading(true); setErr("");
    try {
      const r = await api.post("/ai/market-briefing", { city, force_refresh: force });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load briefing");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchBriefing(false); /* eslint-disable-next-line */ }, [city]);

  return (
    <div className="bg-navy text-white mb-6 overflow-hidden" data-testid="market-briefing">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full p-5 flex items-center justify-between hover:bg-navy-light transition"
        data-testid="mb-toggle"
      >
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-gold" strokeWidth={1.5} />
          <div className="text-left">
            <div className="overline text-gold flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Alberta Market Briefing</div>
            <div className="font-display text-lg">{city === "all" ? "Province-wide snapshot" : `${city} snapshot`}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data?.from_cache && <span className="text-xs text-white/50">cached</span>}
          {loading && <RefreshCw className="w-4 h-4 text-gold animate-spin" />}
          {collapsed ? <ChevronDown className="w-5 h-5 text-white/60" /> : <ChevronUp className="w-5 h-5 text-white/60" />}
        </div>
      </button>

      {!collapsed && (
        <div className="px-5 pb-5" data-testid="mb-content">
          {err && <div className="text-sm text-rose-300 py-4">{err}</div>}
          {!err && !data && !loading && <div className="text-sm text-white/50 py-4">No briefing yet.</div>}
          {data?.data && (
            <div className="space-y-5">
              {data.data.province_summary && (
                <p className="text-sm text-white/80 leading-relaxed italic border-l-2 border-gold pl-4">
                  {data.data.province_summary}
                </p>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(data.data.cities || []).map((c) => (
                  <div key={c.city} className="bg-navy-light p-4" data-testid={`mb-city-${c.city}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-display text-xl text-white">{c.city}</div>
                      <YoYPill pct={c.yoy_change_pct} />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      {Object.entries(c.avg_cad_per_sqft || {}).slice(0, 6).map(([k, v]) => (
                        <div key={k} className="bg-navy/60 p-2">
                          <div className="text-gold text-[9px] uppercase tracking-widest truncate">{k.replace(/_/g, " ")}</div>
                          <div className="text-white font-semibold text-sm">{CAD(v)}</div>
                          <div className="text-white/40 text-[9px]">/sqft CAD</div>
                        </div>
                      ))}
                    </div>
                    {(c.trending_neighbourhoods || []).length > 0 && (
                      <div className="mt-3">
                        <div className="overline text-gold text-[9px] mb-1.5">Trending</div>
                        <div className="flex flex-wrap gap-1.5">
                          {c.trending_neighbourhoods.map((t) => (
                            <button
                              key={t.name}
                              onClick={() => onNeighbourhoodPick?.(c.city, t.name)}
                              title={t.reason}
                              className="text-[10px] px-2 py-1 bg-gold/10 hover:bg-gold/25 border border-gold/30 text-gold transition"
                              data-testid={`mb-trending-${t.name}`}
                            >
                              {t.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {(c.notable_signals || []).length > 0 && (
                      <ul className="mt-3 text-[11px] text-white/70 space-y-0.5">
                        {c.notable_signals.slice(0, 3).map((s, i) => <li key={i}>· {s}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between text-xs text-white/40 pt-3 border-t border-white/10">
                <div>{data.data.disclaimer || "AI-generated estimates — always verify with live MLS data."}</div>
                <button onClick={() => fetchBriefing(true)} disabled={loading} className="text-gold hover:text-gold-light inline-flex items-center gap-1" data-testid="mb-refresh">
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketBriefing;
